import React, { useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import {
  isRelevanceEmail,
  isRequired,
  maxLength,
} from '../../constants/validationFunction';
import useForm from '../../hooks/useForm';

const RECAPTCHA_SITE_KEY = '6LeVGpoUAAAAAPfRJ_2DFqLm5vUe1UxriOVM04rd';

const ContactUsForm = ({ onHide = () => {}, onSubmit = () => {} }) => {
  const { t } = useTranslation();
  const recaptchaRef = useRef(null);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const emptyField = t('contactUsModal.err.empty');
  const invalidEmailField = t('contactUsModal.err.errEmail');
  const invalidPhoneField = t('contactUsModal.err.invalidPhoneField');
  const initialForm = [
    {
      label: `${t('contactUsModal.name')}*`,
      name: 'nameUser',
      validators: [{ func: isRequired, message: emptyField }],
    },
    {
      label: `${t('contactUsModal.email')}*`,
      name: 'emailUser',
      extraParams: { type: 'email' },
      validators: [
        { func: isRequired, message: emptyField },
        { func: isRelevanceEmail, message: invalidEmailField },
      ],
    },
    {
      label: `${t('contactUsModal.number')}`,
      name: 'numberUser',
      extraParams: { type: 'number' },
      validators: [
        {
          func: maxLength,
          message: invalidPhoneField,
          extraParams: { max: 16 },
        },
      ],
    },
    {
      label: `${t('contactUsModal.text')}*`,
      name: 'textareaUser',
      extraParams: { as: 'textarea', rows: 3 },
      validators: [{ func: isRequired, message: emptyField }],
    },
  ];

  const onSubmitHandler = (formData = {}) => {
    const recaptchaToken = recaptchaRef.current?.getValue();
    if (!recaptchaToken) {
      setRecaptchaError(true);
      return;
    }
    setRecaptchaError(false);
    fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...formData, recaptchaToken }),
    })
      .then((res) => res.json())
      .then(() => {
        onHide();
        onSubmit(formData);
        resetFormState();
        recaptchaRef.current?.reset();
      })
      .catch(() => {
        onHide();
        onSubmit(formData);
        resetFormState();
        recaptchaRef.current?.reset();
      });
  };
  const onCancelHandler = () => {
    onHide();
    resetFormState();
  };
  const {
    data,
    errors,
    onChange,
    isValid,
    isInvalid,
    handleSubmit,
    resetFormState,
  } = useForm(onSubmitHandler, initialForm);

  return (
    <Form noValidate onSubmit={handleSubmit}>
      {initialForm.map((fieldGroup) => (
        <Form.Group key={fieldGroup.label}>
          {fieldGroup.label && (
            <Form.Label
              className={
                errors[fieldGroup.name]
                  ? `error-msg `
                  : `${
                      data[fieldGroup.name]?.length
                        ? 'text-gray-light'
                        : 'text-dark'
                    }`
              }
            >
              {fieldGroup.label}
            </Form.Label>
          )}
          <Form.Control
            name={fieldGroup.name}
            isValid={isValid(fieldGroup.name)}
            isInvalid={isInvalid(fieldGroup.name)}
            onChange={onChange}
            className={errors[fieldGroup.name] ? 'error-input' : ''}
            {...fieldGroup.extraParams}
          />
          {fieldGroup.label && errors[fieldGroup.name] && (
            <Form.Label className={'error-msg'}>
              {errors[fieldGroup.name]}
            </Form.Label>
          )}
        </Form.Group>
      ))}

      <Form.Group>
        <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_SITE_KEY} />
        {recaptchaError && (
          <Form.Label className="error-msg">Please confirm you are not a robot</Form.Label>
        )}
      </Form.Group>

      <Form.Group className="form-group-btn">
        <button className={'btn submit'} type="submit">
          {t('contactUsModal.submit')}
        </button>
        <button className={'btn cansel'} onClick={onCancelHandler}>
          {t('contactUsModal.cancel')}
        </button>
      </Form.Group>
    </Form>
  );
};

export default ContactUsForm;
